import React, { useState } from 'react';
import reportsService from '../service/reportsService.mjs';
import { useFinalClaims } from '../hook/useFinalClaims.mjs';
import { saveBlob } from '../util/download.mjs';
import { money } from '../util/format.mjs';
import { claimTotals } from '../util/aggregate.mjs';
import { periodText as periodTextOf, periodTag, namedRegion } from '../util/period.mjs';
import { ScopeToolbar, Section } from './ReportParts.mjs';
import ArchiveTab from './ArchiveTab.mjs';

/**
 * Downloads: the period by kind of claimant (monthly reports, independent
 * contractors, coaches - each laid out like a report archive), every final
 * claim of the period in one zip, then the monthly report archive with each
 * report's own zip and signed PDF.
 *
 * The zip is built by the server one claim at a time and is capped
 * (REPORT_ZIP_MAX_CLAIMS); above the cap it answers 413 with how many
 * matched, and that message is shown as-is so the reader knows how far to
 * narrow the period or region.
 */
const DownloadsTab = ({ scope }) => {
  const { data, claims, loading, error, fiscalYears } = useFinalClaims(scope);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleNotice, setBundleNotice] = useState(null);

  const periodText = periodTextOf(scope.fy, scope.quarter);
  const region = namedRegion(scope, data?.region);
  const regionText = region === 'all' ? 'all regions' : region;
  const totals = claimTotals(claims);

  const downloadZip = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const { blob, filename } = await reportsService.downloadClaimsZip({
        fy: scope.fy, quarter: scope.quarter, region: scope.region,
      });
      saveBlob(blob, filename
        || `Final_Claims_${periodTag(scope.fy, scope.quarter)}_${region === 'all' ? 'All_Regions' : region.replace(/[^\w]+/g, '_')}.zip`);
      setNotice({
        type: 'success',
        text: `Downloaded the final claims for ${regionText}, ${periodText}. MANIFEST.txt inside lists every claim and anything left out.`,
      });
    } catch (err) {
      setNotice({
        type: err.status === 413 ? 'warn' : 'error',
        text: err.status === 413 ? err.message : `Could not build the zip: ${err.message}`,
      });
    } finally {
      setBusy(false);
    }
  };

  const downloadBundle = async () => {
    setBundleBusy(true);
    setBundleNotice(null);
    try {
      const { blob, filename } = await reportsService.downloadReportsBundle({
        fy: scope.fy, quarter: scope.quarter, region: scope.region,
      });
      saveBlob(blob, filename
        || `Seed_reports_${region === 'all' ? 'All-regions' : region.replace(/[^\w]+/g, '_')}_${periodTag(scope.fy, scope.quarter)}.zip`);
      setBundleNotice({
        type: 'success',
        text: `Downloaded the reports for ${regionText}, ${periodText}. MANIFEST.txt inside lists each folder.`,
      });
    } catch (err) {
      setBundleNotice({
        type: err.status === 413 ? 'warn' : 'error',
        text: err.status === 413 ? err.message : `Could not build the download: ${err.message}`,
      });
    } finally {
      setBundleBusy(false);
    }
  };

  const noticeClass = (n) => `mx-4 sm:mx-6 mb-4 px-4 py-3 rounded-lg border ${n.type === 'error'
    ? 'bg-red-50 border-red-200 text-red-700'
    : n.type === 'warn'
      ? 'bg-amber-50 border-amber-200 text-amber-800'
      : 'bg-green-50 border-green-200 text-green-800'}`;

  const countText = loading && !data
    ? 'Counting claims...'
    : error
      ? 'The claim count could not be loaded; the zip can still be built.'
      : `${totals.claims} final claim${totals.claims === 1 ? '' : 's'} (${money(totals.total)}) for ${regionText}, ${periodText}.`;

  return (
    <>
      <ScopeToolbar scope={scope} fiscalYears={fiscalYears} loading={loading} />

      <Section
        title="Reports by claimant type (.zip)"
        subtitle="One zip for the selected region and period, with a folder for each kind of claimant. Monthly reports: staff, each finally approved report exactly as its approval archive is laid out. Independent contractors and Coaches: their claims are finally approved by the auditor and are in no monthly report, so each region and month has a summary PDF and spreadsheet in its place, then a folder per person with their claims, receipts and questions."
      >
        <div className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={downloadBundle}
            disabled={bundleBusy}
            aria-busy={bundleBusy}
            className="min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
          >
            {bundleBusy ? 'Building zip...' : 'Download reports (.zip)'}
          </button>
          <span className="text-sm text-gray-600" role="status">
            {bundleBusy
              ? 'Building the zip - each report is rebuilt with its signatures, which can take a minute or two.'
              : `Monthly reports/, Independent contractors/ and Coaches/ for ${regionText}, ${periodText}.`}
          </span>
        </div>
        {bundleNotice && (
          <div role="status" className={noticeClass(bundleNotice)}>{bundleNotice.text}</div>
        )}
      </Section>

      <Section
        title="All claims in this period (.zip)"
        subtitle="Every final claim for the selected region and period, one folder per person per month: each claim's PDF and receipts, and one Excel workbook with every claim and its PTAE allocation on their own sheets. Drafts and claims still in review are not included."
      >
        <div className="px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <button
            onClick={downloadZip}
            disabled={busy || (!!data && !error && totals.claims === 0)}
            aria-busy={busy}
            className="min-h-[44px] sm:min-h-0 px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors disabled:opacity-50"
          >
            {busy ? 'Building zip...' : 'Download all claims (.zip)'}
          </button>
          <span className="text-sm text-gray-600" role="status">
            {busy ? 'Building the zip - a large period can take a minute or two.' : countText}
          </span>
        </div>
        {notice && (
          <div
            role="status"
            className={`mx-4 sm:mx-6 mb-4 px-4 py-3 rounded-lg border ${notice.type === 'error'
              ? 'bg-red-50 border-red-200 text-red-700'
              : notice.type === 'warn'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-green-50 border-green-200 text-green-800'}`}
          >
            {notice.text}
          </div>
        )}
      </Section>

      <h2 className="text-base font-semibold text-gray-800 mb-3">Monthly report archive</h2>
      <ArchiveTab scope={scope} />
    </>
  );
};

export default DownloadsTab;
